// Layout adapters only: the existing builder owns selection, validation and cart submission.
const make = (tag, className, text) => {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
};
const textValue = input => String(input?.value || '').trim();
const visible = el => Boolean(el && !el.hidden && el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden');

export function currentPurchase(form, catalog, currency) {
  const id = textValue(form.querySelector('input[name="id"]'));
  const variant = catalog.find(item => String(item.id) === id);
  const quantity = Number(form.querySelector('[name="quantity"]')?.value || 1);
  if (!variant || variant.available === false || !Number.isInteger(quantity) || quantity < 1 || !Number.isFinite(variant.price)) return null;
  const language = document.documentElement.lang || 'en';
  const formatterKey = JSON.stringify([language,currency]);
  let formatter = purchaseFormatters.get(formatterKey);
  if (!formatter) {
    formatter = new Intl.NumberFormat(language, {style:'currency',currency,currencyDisplay:'narrowSymbol',minimumFractionDigits:2});
    purchaseFormatters.set(formatterKey,formatter);
  }
  const value = formatter.format(variant.price * quantity / 100);
  return {id, cents:variant.price * quantity, label:`${value} ${currency}`};
}
const purchaseFormatters = new Map();

function photoHelpers(builder) {
  builder.querySelectorAll('.figure-ai-match').forEach(panel => {
    if (panel.closest('.tf-photo-helper')) return;
    const helper = make('details','tf-photo-helper');
    const summary = make('summary');
    summary.append(make('span','tf-photo-helper__title','Find matching pieces from a photo'),make('span','tf-photo-helper__action','Try AI ›'));
    panel.before(helper);
    helper.append(summary,panel);
  });
}

function productProof(builder) {
  if (document.querySelector('[data-tf-product-proof]')) return;
  const widget = document.querySelector('#judgeme_product_reviews, .jdgm-review-widget');
  const review = Array.from(widget?.querySelectorAll('.jdgm-rev[data-verified-buyer="true"]') || []).find(item=>{
    const title=item.querySelector('.jdgm-rev__title')?.textContent.trim();
    const body=item.querySelector('.jdgm-rev__body')?.textContent.trim();
    return Number(item.querySelector('[data-score]')?.dataset.score)===5 && Boolean(title || body) && (title || body).length<=180;
  });
  if(!review) return;
  const quote=review.querySelector('.jdgm-rev__title')?.textContent.trim() || review.querySelector('.jdgm-rev__body').textContent.trim();
  const author=review.querySelector('.jdgm-rev__author')?.textContent.trim() || 'Customer';
  const proof=make('aside','tf-product-proof');proof.dataset.tfProductProof='';
  proof.append(make('q',null,quote));
  const meta=make('p');
  const stars=make('span','tf-product-proof__stars','★★★★★');stars.setAttribute('aria-label','5 out of 5 stars');
  meta.append(stars,document.createTextNode(` ${author} · Verified buyer`));
  const link=make('a',null,'Read product reviews');link.href='#judgeme_product_reviews';
  proof.append(meta,link);
  builder.after(proof);
}

function purchaseBar(builder, form) {
  const root=document.querySelector('[data-tf-byo-mobile-cta]');
  if(!root || root.dataset.layoutReady) return ()=>{};
  const source=Array.from(form.querySelectorAll('.add-to-cart-button,[type="submit"]')).find(el=>!root.contains(el));
  const button=root.querySelector('[data-tf-byo-cta-button]');
  const label=root.querySelector('[data-tf-byo-cta-label]');
  const price=root.querySelector('[data-tf-byo-cta-price]');
  if(!source || !button || !label || !price) return ()=>{};
  root.dataset.layoutReady='true';
  const mobile=matchMedia('(max-width:749px)');
  let reached=false;
  let catalogElement, catalogText, catalog=[];
  const readCatalog=()=>{
    const element=form.querySelector('#bm-variant-json');
    const text=element?.textContent || '[]';
    if(element!==catalogElement || text!==catalogText) {
      catalogElement=element;catalogText=text;
      try {
        const parsed=JSON.parse(text);
        catalog=Array.isArray(parsed)?parsed:[];
      } catch { catalog=[]; /* Invalid catalogue: never retain an earlier total. */ }
    }
    return catalog;
  };
  const render=()=>{
    const bounds=builder.getBoundingClientRect();
    if(bounds.top < innerHeight-100) reached=true;
    const sourceBox=source.getBoundingClientRect();
    const sourceVisible=visible(source) && sourceBox.top<innerHeight-80 && sourceBox.bottom>0;
    const focused=document.activeElement;
    const keyboard=focused?.matches('input:not([type="hidden"]):not([type="checkbox"]):not([type="radio"]),textarea,select,[contenteditable="true"]');
    const overlay=Array.from(document.querySelectorAll('#builderPickerModal,.dialog-modal[open],dialog[open],.cart-drawer.is-open,.drawer--open,header-drawer details[open]')).some(visible);
    const currency=window.Shopify?.currency?.active || price.dataset.currency || 'USD';
    const current=currentPurchase(form,readCatalog(),currency);
    const blocked=source.disabled || source.getAttribute('aria-disabled')==='true' || source.closest('product-form-component')?.getAttribute('aria-busy')==='true';
    const editing=new URLSearchParams(location.search).get('bm_edit')==='1';
    const nextLabel=reached ? (editing?'Update design':button.dataset.cartLabel) : button.dataset.customizeLabel;
    if(label.textContent!==nextLabel) label.textContent=nextLabel;
    const nextPrice=current?.label || 'Check options';
    if(price.textContent!==nextPrice) price.textContent=nextPrice;
    const nextDisabled=blocked || !current;
    if(button.disabled!==nextDisabled) button.disabled=nextDisabled;
    const nextMode=reached?'add':'customize';
    if(button.dataset.mode!==nextMode) button.dataset.mode=nextMode;
    const nextHidden=!mobile.matches || sourceVisible || Boolean(keyboard) || overlay;
    if(root.hidden!==nextHidden) root.hidden=nextHidden;
    const nextSourceVisible=String(sourceVisible);
    if(root.dataset.sourceVisible!==nextSourceVisible) root.dataset.sourceVisible=nextSourceVisible;
    document.body.classList.toggle('tf-byo-mobile-cta-enabled',!root.hidden);
    document.body.classList.toggle('tf-byo-source-visible',mobile.matches && sourceVisible);
  };
  button.addEventListener('click',()=>{
    if(button.disabled) return;
    if(button.dataset.mode==='add') {
      // Show the original validation errors in context; never bypass its handlers.
      source.scrollIntoView({block:'center',behavior:'auto'});
      source.click();
    } else {
      reached=true;builder.scrollIntoView({block:'start',behavior:matchMedia('(prefers-reduced-motion:reduce)').matches?'auto':'smooth'});
    }
    render();
  });
  form.addEventListener('input',render);form.addEventListener('change',render);
  document.addEventListener('focusin',render);document.addEventListener('focusout',()=>requestAnimationFrame(render));
  let viewportFrame=0;
  const scheduleViewportRender=()=>{
    if(viewportFrame) return;
    viewportFrame=requestAnimationFrame(()=>{viewportFrame=0;render();});
  };
  window.addEventListener('scroll',scheduleViewportRender,{passive:true});window.addEventListener('resize',scheduleViewportRender,{passive:true});
  window.visualViewport?.addEventListener('resize',scheduleViewportRender);
  mobile.addEventListener('change',render);
  render();return render;
}

function guideAccordions() {
  document.querySelectorAll('.niche-gift__body,.tf-guide-page').forEach(body=>{
    let inFaq=false;
    Array.from(body.querySelectorAll('h2,h3')).forEach(heading=>{
      if(heading.tagName==='H2'){inFaq=/common questions|frequently asked|faq/i.test(heading.textContent);return;}
      if(!inFaq || heading.tagName!=='H3') return;
      const details=make('details','tf-guide-question');
      const summary=make('summary',null,heading.textContent);details.append(summary);
      heading.before(details);
      let next=heading.nextElementSibling;
      while(next && !/^H[23]$/.test(next.tagName) && next.tagName!=='SCRIPT') {const following=next.nextElementSibling;details.append(next);next=following;}
      heading.remove();
    });
  });
}

function customerGalleries() {
  document.querySelectorAll('[data-tf-customer-gallery]').forEach(gallery=>{
    const filters=gallery.querySelector('[data-tf-gallery-filters]');
    if(!filters || gallery.dataset.ready) return;
    gallery.dataset.ready='true';
    const cards=Array.from(gallery.querySelectorAll('[data-tf-customer-photo]'));
    const buttons=Array.from(filters.querySelectorAll('[data-tf-gallery-filter]'));
    const status=gallery.querySelector('[data-tf-gallery-status]');
    const inFormat=(card,mode)=>(card.dataset.format||'').split(' ').includes(mode);
    const filter=mode=>{
      let count=0;
      cards.forEach(card=>{card.hidden=mode!=='all' && !inFormat(card,mode);if(!card.hidden) count++;});
      buttons.forEach(button=>button.setAttribute('aria-pressed',String(button.dataset.tfGalleryFilter===mode)));
      if(status) status.textContent=`${count} customer ${count===1?'photo':'photos'}. Written reviews remain below.`;
    };
    buttons.forEach(button=>{
      const mode=button.dataset.tfGalleryFilter;
      button.hidden=mode!=='all' && !cards.some(card=>inFormat(card,mode));
      button.addEventListener('click',()=>filter(mode));
    });
    filters.hidden=false;
    filter('all');
  });
}

export function initLayout() {
  guideAccordions();
  customerGalleries();
  const builder=document.querySelector('#smartPersonalization');
  const form=builder?.closest('form');
  if(!builder || !form || builder.dataset.tfLayoutReady) return;
  builder.dataset.tfLayoutReady='true';
  document.querySelectorAll('[data-tf-design-summary]').forEach(section=>section.remove());
  const purchase=purchaseBar(builder,form);
  let scheduled=false;
  const sync=()=>{
    if(scheduled) return;scheduled=true;
    requestAnimationFrame(()=>{scheduled=false;photoHelpers(builder);purchase();productProof(builder);});
  };
  form.addEventListener('input',sync);form.addEventListener('change',sync);
  new MutationObserver(sync).observe(builder,{subtree:true,childList:true,attributes:true,attributeFilter:['hidden','class','src','value']});
  // Native hidden-input .value setters do not emit DOM events; observe the existing computed variant.
  const timer=setInterval(()=>{if(!builder.isConnected){clearInterval(timer);return;}if(!document.hidden) sync();},400);
  photoHelpers(builder);purchase();productProof(builder);
}
if(typeof document!=='undefined') {
  if(document.readyState==='loading') document.addEventListener('DOMContentLoaded',initLayout,{once:true});
  else initLayout();
  document.addEventListener('shopify:section:load',initLayout);
}
